"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import { Users, Search, MapPin, Briefcase, Calendar, UserPlus, UserCheck, MessageSquare, Filter, ChevronDown } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { useNavigate } from "react-router-dom"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
`

const Header = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  box-shadow: ${props => props.theme.shadow};
  padding: 24px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: ${props => props.theme.textPrimary};
`

const Subtitle = styled.p`
  font-size: 16px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 20px 0;
`

const SearchSection = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const SearchBar = styled.div`
  position: relative;
  flex: 1;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  transition: border-color 0.2s;
  
  &:focus {
    border-color: ${props => props.theme.buttonPrimary};
  }
  
  &::placeholder {
    color: ${props => props.theme.textMuted};
  }
`

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.textMuted};
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
    color: ${props => props.theme.buttonPrimary};
  }
`

const Stats = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
`

const PeopleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
`

const PersonCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadowHover};
  }
`

const PersonHeader = styled.div`
  padding: 20px 20px 16px 20px;
  text-align: center;
  background-color: ${props => props.theme.hoverBackground};
  border-bottom: 1px solid ${props => props.theme.border};
  position: relative;
`

const PersonAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${props => props.theme.border};
  background-image: ${props => props.$avatar ? `url(${props.$avatar})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px auto;
  border: 3px solid ${props => props.theme.border};
  cursor: pointer;
`

const PersonName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
`

const PersonUsername = styled.div`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
`

const PersonBody = styled.div`
  padding: 20px;
`

const PersonBio = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 16px 0;
  line-height: 1.5;
`

const PersonMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
`

const PersonStats = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 12px 0;
  border-top: 1px solid ${props => props.theme.border};
  border-bottom: 1px solid ${props => props.theme.border};
  margin-bottom: 16px;
`

const StatItem = styled.div`
  text-align: center;
`

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
`

const StatLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  margin-top: 2px;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`

const FollowButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background-color: ${props => props.$isFollowing ? props.theme.borderLight : props.theme.buttonPrimary};
  color: ${props => props.$isFollowing ? props.theme.textSecondary : 'white'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.$isFollowing ? props.theme.hoverBackground : props.theme.buttonPrimaryHover};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const MessageButton = styled.button`
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
    color: ${props => props.theme.buttonPrimary};
  }
`

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
    color: ${props => props.theme.buttonPrimary};
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${props => props.theme.textSecondary};
  
  .icon {
    margin-bottom: 12px;
    color: ${props => props.theme.textMuted};
  }
`

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${props => props.theme.textSecondary};
`

export default function PeoplePage() {
  const { isLoggedIn } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [people, setPeople] = useState([])
  const [filteredPeople, setFilteredPeople] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [followingStates, setFollowingStates] = useState({})
  const [followLoading, setFollowLoading] = useState({})
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    fetchPeople(true)
  }, [])

  useEffect(() => {
    filterPeople()
  }, [searchQuery, people])

  const fetchPeople = async (reset = false) => {
    setLoading(true)
    try {
      const currentPage = reset ? 1 : page
      const response = await fetch(`/user/recommended?limit=12&page=${currentPage}`, {
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          const newPeople = data.users || []
          
          if (reset) {
            setPeople(newPeople)
            setPage(2)
          } else {
            setPeople(prev => [...prev, ...newPeople])
            setPage(prev => prev + 1)
          }
          
          setHasMore(newPeople.length === 12)
          setTotalCount(data.total || newPeople.length)
          checkFollowStates(newPeople)
        }
      } else {
        // 더미 데이터 사용
        const dummyPeople = generateDummyPeople(12)
        setPeople(dummyPeople)
        setTotalCount(dummyPeople.length)
        checkFollowStates(dummyPeople)
      }
    } catch (error) {
      console.error('사용자 목록 로드 오류:', error)
      // 더미 데이터 사용
      const dummyPeople = generateDummyPeople(12)
      setPeople(dummyPeople)
      setTotalCount(dummyPeople.length)
      checkFollowStates(dummyPeople)
    } finally {
      setLoading(false)
    }
  }

  const generateDummyPeople = (count) => {
    const names = ['김민수', '이영희', '박철수', '정수진', '최영수', '한미영', '임재현', '송미라', '윤도현', '강은영', '조현우', '배수지']
    const jobs = ['Frontend Developer', 'Backend Developer', 'UI/UX Designer', 'Product Manager', 'DevOps Engineer', 'Data Scientist']
    const companies = ['네이버', '카카오', '삼성', '엘지', '현대', '스타트업']
    const locations = ['서울', '부산', '대구', '인천', '광주', '대전']
    
    return Array.from({ length: count }, (_, i) => ({
      id: i + 1,
      username: `user_${i + 1}`,
      full_name: names[i % names.length],
      avatar_url: null,
      bio: `${jobs[i % jobs.length]} at ${companies[i % companies.length]}`,
      location: locations[i % locations.length],
      post_count: Math.floor(Math.random() * 100) + 5,
      follower_count: Math.floor(Math.random() * 500) + 10,
      following_count: Math.floor(Math.random() * 300) + 5
    }))
  }

  const checkFollowStates = async (usersList) => {
    if (!isLoggedIn) return
    
    const states = {}
    for (const user of usersList) {
      try {
        const response = await fetch(`/user/follow-status/${user.id}`, {
          credentials: 'include'
        })
        if (response.ok) {
          const data = await response.json()
          states[user.id] = data.isFollowing
        }
      } catch (error) {
        console.error(`팔로우 상태 확인 오류 (${user.id}):`, error)
        states[user.id] = false
      }
    }
    setFollowingStates(prev => ({ ...prev, ...states }))
  }

  const handleFollow = async (userId) => {
    if (!isLoggedIn) {
      alert('로그인이 필요합니다.')
      return
    }
    
    setFollowLoading(prev => ({ ...prev, [userId]: true }))
    try {
      const response = await fetch(`/user/follow/${userId}`, {
        method: 'POST',
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setFollowingStates(prev => ({
            ...prev,
            [userId]: data.isFollowing
          }))
        }
      }
    } catch (error) {
      console.error('팔로우 오류:', error)
    } finally {
      setFollowLoading(prev => ({ ...prev, [userId]: false }))
    }
  }

  const filterPeople = () => {
    if (!searchQuery.trim()) {
      setFilteredPeople(people)
      return
    }
    
    const filtered = people.filter(person => 
      person.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      person.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      person.bio?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    setFilteredPeople(filtered)
  }

  const handleUserClick = (username) => {
    navigate(`/profile/${username}`)
  }

  const handleLoadMore = () => {
    fetchPeople(false)
  }

  return (
    <Container>
      <Header theme={theme}>
        <Title theme={theme}>사람 찾기</Title>
        <Subtitle theme={theme}>새로운 동료들을 만나고 네트워크를 확장해보세요</Subtitle>
        
        <SearchSection>
          <SearchBar>
            <SearchIcon theme={theme}>
              <Search size={20} />
            </SearchIcon>
            <SearchInput
              theme={theme}
              type="text"
              placeholder="이름, 사용자명, 직업으로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBar>
          
          <FilterButton theme={theme}>
            <Filter size={16} />
            필터
            <ChevronDown size={16} />
          </FilterButton>
        </SearchSection>
        
        <Stats theme={theme}>
          <span><Users size={16} /> 총 {totalCount.toLocaleString()}명의 사용자</span>
          <span>• 활발한 커뮤니티</span>
        </Stats>
      </Header>

      {loading && people.length === 0 ? (
        <LoadingState theme={theme}>사용자 정보를 불러오는 중...</LoadingState>
      ) : filteredPeople.length === 0 ? (
        <EmptyState theme={theme}>
          <Users size={48} className="icon" />
          <h3>검색 결과가 없습니다</h3>
          <p>다른 키워드로 검색해보세요</p>
        </EmptyState>
      ) : (
        <>
          <PeopleGrid>
            {filteredPeople.map((person) => (
              <PersonCard key={person.id} theme={theme}>
                <PersonHeader theme={theme}>
                  <PersonAvatar 
                    theme={theme}
                    $avatar={person.avatar_url}
                    onClick={() => handleUserClick(person.username)}
                  >
                    {!person.avatar_url && <Users size={32} />}
                  </PersonAvatar>
                  
                  <PersonName theme={theme} onClick={() => handleUserClick(person.username)}>
                    {person.full_name || person.username}
                  </PersonName>
                  
                  <PersonUsername theme={theme} onClick={() => handleUserClick(person.username)}>
                    @{person.username}
                  </PersonUsername>
                </PersonHeader>

                <PersonBody>
                  {person.bio && (
                    <PersonBio theme={theme}>{person.bio}</PersonBio>
                  )}
                  
                  {(person.location || person.company) && (
                    <PersonMeta>
                      {person.location && (
                        <MetaItem theme={theme}>
                          <MapPin size={14} />
                          {person.location}
                        </MetaItem>
                      )}
                      {person.company && (
                        <MetaItem theme={theme}>
                          <Briefcase size={14} />
                          {person.company}
                        </MetaItem>
                      )}
                    </PersonMeta>
                  )}

                  <PersonStats theme={theme}>
                    <StatItem>
                      <StatValue theme={theme}>{person.post_count || 0}</StatValue>
                      <StatLabel theme={theme}>게시물</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue theme={theme}>{person.follower_count || 0}</StatValue>
                      <StatLabel theme={theme}>팔로워</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue theme={theme}>{person.following_count || 0}</StatValue>
                      <StatLabel theme={theme}>팔로잉</StatLabel>
                    </StatItem>
                  </PersonStats>

                  <ActionButtons>
                    <FollowButton
                      theme={theme}
                      $isFollowing={followingStates[person.id]}
                      disabled={followLoading[person.id] || !isLoggedIn}
                      onClick={() => handleFollow(person.id)}
                    >
                      {followLoading[person.id] ? (
                        '처리중...'
                      ) : followingStates[person.id] ? (
                        <>
                          <UserCheck size={14} />
                          팔로잉
                        </>
                      ) : (
                        <>
                          <UserPlus size={14} />
                          팔로우
                        </>
                      )}
                    </FollowButton>
                    
                    <MessageButton 
                      theme={theme}
                      onClick={() => navigate(`/messages?user=${person.username}`)}
                    >
                      <MessageSquare size={16} />
                    </MessageButton>
                  </ActionButtons>
                </PersonBody>
              </PersonCard>
            ))}
          </PeopleGrid>

          {hasMore && !searchQuery && (
            <LoadMoreButton theme={theme} onClick={handleLoadMore} disabled={loading}>
              {loading ? '로딩 중...' : '더 많은 사용자 보기'}
            </LoadMoreButton>
          )}
        </>
      )}
    </Container>
  )
}